/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Validation outcome for an address submitted to the address service. */
export type AddressValidationResult =
  /** The address is in an area not yet available for use. */
  | 'ADDRESS_COMING_SOON'
  /** The input contains unnecessary sub-premise or other information. */
  | 'ADDRESS_EXTRANEOUS_INPUT'
  /** The address is invalid. */
  | 'ADDRESS_INVALID'
  /** The box number is invalid. */
  | 'ADDRESS_INVALID_BOX_NUMBER'
  /** The street number or premise information is invalid. */
  | 'ADDRESS_INVALID_PREMISE'
  /** The street portion of the address is invalid. */
  | 'ADDRESS_INVALID_STREET'
  /** The unit or sub-premise information is invalid. */
  | 'ADDRESS_INVALID_SUB_PREMISE'
  /** The box number is missing. */
  | 'ADDRESS_MISSING_BOX_NUMBER'
  /** The street number or premise information is missing. */
  | 'ADDRESS_MISSING_PREMISE'
  /** Required unit or sub-premise information is missing. */
  | 'ADDRESS_MISSING_SUB_PREMISE'
  /** The input is unusable and does not identify a meaningful address. */
  | 'ADDRESS_NIL'
  /** The input does not identify a real deliverable address. */
  | 'ADDRESS_NOT_REAL'
  /** The address is outside the supported geography or address types. */
  | 'ADDRESS_NOT_SUPPORTED'
  /** The address validation service has no known result. */
  | 'ADDRESS_UNKNOWN'
  /** The address is recognized but currently unoccupied. */
  | 'ADDRESS_VACANT'
  /** The address is fully valid. */
  | 'ADDRESS_VALID'
  /** The address is valid only through its street-level location. */
  | 'ADDRESS_VALID_STREET';

/** Reference to an existing property address for the seller application. */
export type ExistingSellerApplicationAddressInput = {
  /** Identifier of the existing property address. */
  addressId: Types.Scalars['UUID']['input'];
};

/** Property address entered manually for the seller application. */
export type ManualSellerApplicationAddressInput = {
  /** City where the property is located. */
  city: Types.Scalars['NonEmptyString']['input'];
  /** Postal code for the property. */
  postalCode: Types.Scalars['PostalCode']['input'];
  /** State or province where the property is located. */
  state: Types.Scalars['NonEmptyString']['input'];
  /** Street address of the property. */
  street: Types.Scalars['NonEmptyString']['input'];
  /** Unit or apartment number when the property has one. */
  unit?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Whether the user explicitly confirmed this address after reviewing an address warning. */
  userConfirmed?: Types.Scalars['Boolean']['input'] | null | undefined;
};

/** The product path selected when the seller entered an address. */
export type SellerAddressEntryProduct =
  /** The seller entered through the Opendoor Complete experience. */
  | 'OPENDOOR_COMPLETE'
  /** The seller entered through the Opendoor Exclusives experience. */
  | 'OPENDOOR_EXCLUSIVES'
  /** The seller entered through a direct cash-offer path. */
  | 'SELL_DIRECT';

/** Confirmation of the address shown during address entry. */
export type SellerApplicationAddressEntryConfirmationStepInput = {
  /** Relationship the user confirmed for this property, when collected. */
  ownerRelationship?: SellerApplicationOwnerRelationship | null | undefined;
};

/** Product path and URL used to start a seller application. */
export type SellerApplicationAddressEntryInput = {
  /** Product path selected for the address entry; omit when not known. */
  product?: SellerAddressEntryProduct | null | undefined;
  /** URL where the seller entered the application. */
  url: Types.Scalars['URL']['input'];
};

/** Exactly one existing or manually entered property address. */
export type SellerApplicationAddressInput =
  {   /** Existing property address to reuse. */
  existingAddress: ExistingSellerApplicationAddressInput; manualAddress?: never; }
  |  { existingAddress?: never;   /** Property address entered manually. */
  manualAddress: ManualSellerApplicationAddressInput; };

/** Anonymous attribution and prior-application context for application entry. */
export type SellerApplicationAttributionInput = {
  /** Anonymous identifier for the application session. */
  anonymousId: Types.Scalars['UUID']['input'];
  /** Previous seller application to associate with this attempt. */
  previousApplicationId?: Types.Scalars['UUID']['input'] | null | undefined;
  /** Attribution events in the order they were tracked; defaults to an empty list. */
  trackingEvents?: Array<SellerApplicationTrackingEventInput>;
};

/** The seller’s interest in buying another home. */
export type SellerApplicationBuyingInterestStepInput = {
  /** Whether the seller intends to buy another home. */
  buyingIntent: Types.Scalars['Boolean']['input'];
};

/** The seller’s condition rating for a room or exterior area. */
export type SellerApplicationConditionRating =
  /** The room is functional but has older finishes or features. */
  | 'DATED'
  /** The room needs substantial repairs or renovation. */
  | 'FIXER_UPPER'
  /** The room has notably upgraded finishes or features. */
  | 'HIGH_END'
  /** The room has premium finishes or features. */
  | 'LUXURY'
  /** The room has typical, maintained finishes and features. */
  | 'STANDARD';

/** Room-level condition ratings selected by the seller. */
export type SellerApplicationConditionStepInput = {
  /** Condition rating for the bathroom. */
  bathroom: SellerApplicationConditionRating;
  /** Condition rating for the exterior. */
  exterior: SellerApplicationConditionRating;
  /** Condition rating for the kitchen. */
  kitchen: SellerApplicationConditionRating;
  /** Condition rating for the living room. */
  livingRoom: SellerApplicationConditionRating;
};

/** Home facts confirmed by the seller in onboarding. */
export type SellerApplicationConfirmStepInput = {
  /** Above-grade home area in square feet. */
  aboveGradeSqFt: Types.Scalars['SafeInt']['input'];
  /** Number of full bathrooms. */
  bathroomsFull: Types.Scalars['SafeInt']['input'];
  /** Number of half bathrooms. */
  bathroomsHalf: Types.Scalars['SafeInt']['input'];
  /** Number of bedrooms. */
  bedrooms: Types.Scalars['SafeInt']['input'];
  /** Dwelling type of the property. */
  dwellingType: SellerApplicationDwellingType;
  /** Entry arrangement for a non-single-family home. */
  entryType?: SellerApplicationEntryType | null | undefined;
  /** Year the home was built. */
  yearBuilt: Types.Scalars['SafeInt']['input'];
};

/** Seller situation, HOA, utilities, and eligibility answers. */
export type SellerApplicationDetailsStepInput = {
  /** Whether ownership is subject to below-market-rate restrictions. */
  belowMarketRateOwnership?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Budget for a future home purchase; required when buyingIntent is true. */
  buyingBudget?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Whether the seller intends to buy another home. */
  buyingIntent?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Whether the property uses a cesspool. */
  cesspool?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Whether the property belongs to a homeowners association. */
  hasHoa: Types.Scalars['Boolean']['input'];
  /** Monthly homeowners association fees in dollars; required when hasHoa is true. */
  hoaFees?: Types.Scalars['Float']['input'] | null | undefined;
  /** Guard status for a gated HOA community. */
  hoaGuard?: SellerApplicationHoaGuard | null | undefined;
  /** Homeowners association community type. */
  hoaType?: SellerApplicationHoaType | null | undefined;
  /** Selected homebuilder partner identifier; required when workingWithHomeBuilder is true. */
  homebuilderName?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Whether the property has leased solar panels. */
  leasedSolarPanels?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Outstanding mortgage balance. */
  mortgageBalance?: Types.Scalars['Float']['input'] | null | undefined;
  /** Expected timing for selling the home. */
  saleTimeline: SellerTimeline;
  /** Whether the property uses a septic system. */
  septic: Types.Scalars['Boolean']['input'];
  /** Whether the property has an underground oil tank. */
  undergroundOilTank?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Whether the property has a unique ownership structure. */
  uniqueOwnershipStructure?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Whether the property uses well water. */
  wellWater: Types.Scalars['Boolean']['input'];
  /** Whether the seller is working with a homebuilder. */
  workingWithHomeBuilder?: Types.Scalars['Boolean']['input'] | null | undefined;
};

/** The dwelling type of the seller property. */
export type SellerApplicationDwellingType =
  /** An apartment home. */
  | 'APARTMENT'
  /** A commercial property. */
  | 'COMMERCIAL'
  /** A mobile home. */
  | 'MOBILE_HOME'
  /** A property with multiple residential units. */
  | 'MULTI_FAMILY'
  /** A detached or attached single-family home. */
  | 'SINGLE_FAMILY'
  /** A townhouse-style home. */
  | 'TOWNHOUSE'
  /** A vacant property without a dwelling. */
  | 'VACANT';

/** A property condition or ownership constraint considered during offer intake. */
export type SellerApplicationEligibilityCriterion =
  /** The home is subject to below-market-rate ownership restrictions. */
  | 'BELOW_MARKET_RATE_OWNERSHIP'
  /** The property uses a cesspool. */
  | 'CESSPOOL'
  /** The home has fire damage. */
  | 'FIRE_DAMAGE'
  /** The home has known foundation issues. */
  | 'KNOWN_FOUNDATION_ISSUES'
  /** The home has leased or financed solar panels. */
  | 'LEASED_SOLAR_PANELS'
  /** The home is mobile or manufactured. */
  | 'MOBILE_MANUFACTURED_HOME'
  /** The home is rent controlled and currently tenant occupied. */
  | 'RENT_CONTROLLED_TENANT_OCCUPIED'
  /** The property uses a septic system. */
  | 'SEPTIC'
  /** The property has an active or inactive underground fuel oil tank. */
  | 'UNDERGROUND_OIL_TANK'
  /** The home has a nonstandard ownership structure. */
  | 'UNIQUE_OWNERSHIP_STRUCTURE'
  /** The property uses well water. */
  | 'WELL_WATER';

/** Eligibility conditions selected for the seller property. */
export type SellerApplicationEligibilityStepInput = {
  /** Whether the seller owns the land; required only when the mobile or manufactured home criterion is selected. */
  ownsMobileManufacturedHomeLand?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Conditions that apply to the property; an empty list means none apply. */
  selectedCriteria: Array<SellerApplicationEligibilityCriterion>;
};

/** How the seller enters the property during application intake. */
export type SellerApplicationEntryType =
  /** The home has its own direct entrance. */
  | 'DIRECT_ENTRY'
  /** The home is a condo with a shared entrance. */
  | 'SHARED_ENTRANCE_CONDO';

/** Home features selected by the seller in onboarding. */
export type SellerApplicationFeaturesStepInput = {
  /** Home features the seller selected. */
  selectedFeatures: Array<SellerApplicationHomeFeature>;
};

/** Whether a gated homeowners association community has a guard. */
export type SellerApplicationHoaGuard =
  /** The gated community has a guard. */
  | 'HAS_GUARD'
  /** The gated community does not have a guard. */
  | 'NO_GUARD';

/** The homeowners association community type for the seller property. */
export type SellerApplicationHoaType =
  /** The home is in an age-restricted homeowners association community. */
  | 'AGE_RESTRICTED_COMMUNITY'
  /** The home is in a gated homeowners association community. */
  | 'GATED_COMMUNITY'
  /** The home is not in a homeowners association. */
  | 'NONE';

/** A home feature selected during seller application intake. */
export type SellerApplicationHomeFeature =
  /** An above-ground pool. */
  | 'ABOVE_GROUND_POOL'
  /** A bathroom attached to a bedroom. */
  | 'ATTACHED_BATHROOM'
  /** A remodeled bathroom. */
  | 'BATHROOM_REMODEL'
  /** A community pool. */
  | 'COMMUNITY_POOL'
  /** A corner lot or cul-de-sac location. */
  | 'CORNER_OR_CULDESAC'
  /** A deck or patio. */
  | 'DECK_PATIO'
  /** A dedicated laundry room. */
  | 'DEDICATED_LAUNDRY_ROOM'
  /** A dining room. */
  | 'DINING_ROOM'
  /** A dock. */
  | 'DOCK'
  /** A double oven. */
  | 'DOUBLE_OVEN'
  /** A double bathroom vanity. */
  | 'DOUBLE_VANITY'
  /** Energy-efficient windows. */
  | 'EFFICIENT_WINDOWS'
  /** Electric-vehicle charging. */
  | 'EV_CHARGING'
  /** A fenced backyard. */
  | 'FENCED_BACKYARD'
  /** A fireplace. */
  | 'FIREPLACE'
  /** Fresh interior or exterior paint. */
  | 'FRESH_PAINT'
  /** Storage in the garage. */
  | 'GARAGE_STORAGE'
  /** A basement. */
  | 'HAS_BASEMENT'
  /** High-end kitchen appliances. */
  | 'HIGH_END_APPLIANCES'
  /** High-end interior finishes. */
  | 'HIGH_END_FINISHES'
  /** A dedicated home office. */
  | 'HOME_OFFICE'
  /** A hot tub. */
  | 'HOT_TUB'
  /** An integrated sprinkler system. */
  | 'INTEGRATED_SPRINKLER'
  /** An in-ground pool. */
  | 'IN_GROUND_POOL'
  /** A kitchen island. */
  | 'KITCHEN_ISLAND'
  /** A remodeled kitchen. */
  | 'KITCHEN_REMODEL'
  /** A large driveway. */
  | 'LARGE_DRIVEWAY'
  /** A loft or flexible-use room. */
  | 'LOFT_OR_FLEX_ROOM'
  /** Maintained landscaping. */
  | 'MAINTAINED_LANDSCAPING'
  /** Modern home fixtures. */
  | 'MODERN_FIXTURES'
  /** Notable natural light. */
  | 'NATURAL_LIGHT'
  /** New flooring. */
  | 'NEW_FLOORING'
  /** A new heating or cooling system. */
  | 'NEW_HVAC'
  /** A new roof. */
  | 'NEW_ROOF'
  /** A kitchen with an open layout. */
  | 'OPEN_CONCEPT_KITCHEN'
  /** A room that opens to the kitchen. */
  | 'OPEN_TO_KITCHEN'
  /** An outdoor kitchen or fire pit. */
  | 'OUTDOOR_KITCHEN_OR_FIREPIT'
  /** Premium appliance finishes. */
  | 'PREMIUM_APPLIANCE_FINISH'
  /** Premium kitchen countertops. */
  | 'PREMIUM_COUNTERTOPS'
  /** Premium flooring in the home. */
  | 'PREMIUM_FLOORING'
  /** Premium flooring in the living area. */
  | 'PREMIUM_FLOORING_LIVING'
  /** A quiet location. */
  | 'QUIET'
  /** Security cameras. */
  | 'SECURITY_CAMERAS'
  /** A smart thermostat. */
  | 'SMART_THERMOSTAT'
  /** A soaking tub. */
  | 'SOAKING_TUB'
  /** Solar panels or another solar feature. */
  | 'SOLAR'
  /** A tankless water heater. */
  | 'TANKLESS_WATER_HEATER'
  /** A two-car garage. */
  | 'TWO_CAR_GARAGE'
  /** Updated toilets. */
  | 'UPDATED_TOILETS'
  /** Vaulted ceilings. */
  | 'VAULTED_CEILINGS'
  /** A video doorbell or smart locks. */
  | 'VIDEO_DOORBELL_OR_SMART_LOCKS'
  /** A walkable location. */
  | 'WALKABLE'
  /** A walk-in closet. */
  | 'WALK_IN_CLOSET'
  /** A walk-in pantry. */
  | 'WALK_IN_PANTRY'
  /** A walk-in shower. */
  | 'WALK_IN_SHOWER';

/** The seller’s current optional homebuilder details. This is a full snapshot; omitted or null fields are cleared. */
export type SellerApplicationHomebuilderDetailsStepInput = {
  /** Homebuilder community name, up to 255 characters; omit or pass null to clear it. */
  communityName?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /**
   * Deprecated alias for salesAssociateEmail.
   * @deprecated Use salesAssociateEmail.
   */
  email?: Types.Scalars['EmailAddress']['input'] | null | undefined;
  /** Email address for the homebuilder sales associate; omit or pass null to clear it. */
  salesAssociateEmail?: Types.Scalars['EmailAddress']['input'] | null | undefined;
};

/** The seller’s current relationship with a homebuilder. */
export type SellerApplicationHomebuilderRelationshipStepInput = {
  /** Whether the seller is working with a homebuilder; false also clears stored builder details. */
  workingWithHomeBuilder: Types.Scalars['Boolean']['input'];
};

/** The seller’s current homebuilder selection. This is a full snapshot; an omitted or null value clears the selection. */
export type SellerApplicationHomebuilderSelectionStepInput = {
  /**
   * Deprecated alias for partnerIdentifier.
   * @deprecated Use partnerIdentifier.
   */
  homebuilderName?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Identifier of the selected homebuilder partner; omit or pass null to clear the selection. */
  partnerIdentifier?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
};

/** The seller’s interest in an Opendoor Home Loans mortgage quote. */
export type SellerApplicationMortgageQuoteInterestStepInput = {
  /** Whether the seller wants a mortgage quote and consents to being contacted about mortgage products. */
  interested: Types.Scalars['Boolean']['input'];
};

/** The user’s relationship to the owner of the seller property. */
export type SellerApplicationOwnerRelationship =
  /** The user represents the property owner as an agent. */
  | 'AGENT'
  /** The user owns the property and also represents it as an agent. */
  | 'AGENT_AND_OWNER'
  /** The user has another relationship to the property owner. */
  | 'OTHER'
  /** The user is the property owner. */
  | 'SELF';

/** A category of home repair issues reviewed during application intake. */
export type SellerApplicationRepairCategory =
  /** Fire damage, smoke, or strong odors. */
  | 'FIRE'
  /** Flooring and cabinet or countertop condition. */
  | 'FLOORING'
  /** Heating, cooling, electrical, and wiring condition. */
  | 'HVAC'
  /** Lot, slope, flooding, noise, and neighboring properties. */
  | 'LOCATION_LOT'
  /** Mold, pests, and chemical odors. */
  | 'MOLD_PESTS'
  /** Driveway, deck, and landscaping condition. */
  | 'OUTDOOR_SPACE'
  /** Drains, pipes, and water pressure. */
  | 'PLUMBING'
  /** Roof, siding, and exterior wood condition. */
  | 'ROOF_SIDING'
  /** Floor slopes, cracks, doors, windows, and structural movement. */
  | 'STRUCTURE'
  /** Walls, windows, and railings. */
  | 'WALLS'
  /** Water damage, leaks, mold, or floor moisture. */
  | 'WATER';

/** Repair issues selected for a single onboarding repair category. */
export type SellerApplicationRepairGroupInput = {
  /** Repair category being reviewed. */
  category: SellerApplicationRepairCategory;
  /** Whether the seller found no issues in this category. */
  noIssues: Types.Scalars['Boolean']['input'];
  /** Repair issues selected in this category; empty when noIssues is true. */
  selectedIssues: Array<SellerApplicationRepairIssue>;
};

/** A specific home repair issue selected during application intake. */
export type SellerApplicationRepairIssue =
  /** Active leaks. */
  | 'ACTIVE_LEAKS'
  /** Bowing walls. */
  | 'BOWING_WALLS'
  /** Broken windows. */
  | 'BROKEN_WINDOWS'
  /** Damaged cabinets. */
  | 'CABINETS_DAMAGED'
  /** A chemical smell. */
  | 'CHEMICAL_SMELL'
  /** Damaged countertops. */
  | 'COUNTERTOPS_DAMAGED'
  /** Doors or windows that do not close. */
  | 'DOORS_WINDOWS_DONT_CLOSE'
  /** Driveway cracks. */
  | 'DRIVEWAY_CRACKS'
  /** Electrical issues. */
  | 'ELECTRICAL_ISSUES'
  /** Exposed wiring. */
  | 'EXPOSED_WIRING'
  /** A flood-prone area. */
  | 'FLOOD_AREA'
  /** Worn flooring. */
  | 'FLOORING_WORN'
  /** Holes in drywall. */
  | 'HOLES_DRYWALL'
  /** A heating or cooling system that does not work. */
  | 'HVAC_NOT_WORKING'
  /** Large cracks. */
  | 'LARGE_CRACKS'
  /** Missing railings. */
  | 'MISSING_RAILINGS'
  /** Mold. */
  | 'MOLD'
  /** A noisy location. */
  | 'NOISY_LOCATION'
  /** Old pipes. */
  | 'OLD_PIPES'
  /** A pest infestation. */
  | 'PEST_INFESTATION'
  /** Poor landscaping. */
  | 'POOR_LANDSCAPING'
  /** Roof damage. */
  | 'ROOF_DAMAGE'
  /** Roof leaks. */
  | 'ROOF_LEAKS'
  /** Rotting exterior wood. */
  | 'ROTTING_WOOD'
  /** Run-down neighboring properties. */
  | 'RUNDOWN_NEIGHBORS'
  /** Sloping floors. */
  | 'SLOPING_FLOORS'
  /** Slow drains. */
  | 'SLOW_DRAINS'
  /** Smoke or fire damage. */
  | 'SMOKE_FIRE'
  /** Spongy floors. */
  | 'SPONGY_FLOORS'
  /** Steep slopes. */
  | 'STEEP_SLOPES'
  /** A strong odor. */
  | 'STRONG_ODOR'
  /** Unstable decks. */
  | 'UNSTABLE_DECKS'
  /** Visible mold. */
  | 'VISIBLE_MOLD'
  /** Low or inconsistent water pressure. */
  | 'WATER_PRESSURE'
  /** Water stains. */
  | 'WATER_STAINS';

/** Repair issue groups reviewed by the seller in onboarding. */
export type SellerApplicationRepairsStepInput = {
  /** One repair issue group for each supported category. */
  groups: Array<SellerApplicationRepairGroupInput>;
};

/** Property, attribution, and journey context for starting a seller application. */
export type SellerApplicationStartInput = {
  /** Property address for the application. */
  address: SellerApplicationAddressInput;
  /** Product path and URL used to enter the application. */
  addressEntry: SellerApplicationAddressEntryInput;
  /** Anonymous attribution context for the application. */
  attribution: SellerApplicationAttributionInput;
  /** Lead acquisition channel; omit when no specific channel applies. */
  channel?: SellerLeadChannel | null | undefined;
  /** Whether the application should use the COVID-19 whitelist path. */
  covid19Whitelist?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Identifier of the selling flow; omit when not applicable. */
  flowId?: Types.Scalars['UUID']['input'] | null | undefined;
  /** Name of the selling flow; omit when not applicable. */
  flowName?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Partner referral identifier; omit when not referred by a partner. */
  partnerReferralId?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
};

/** Exactly one completed seller application step to persist. */
export type SellerApplicationStepInput =
  {   /** Confirms the address shown during address entry and optionally captures the user’s relationship to its owner without advancing onboarding progress. */
  addressEntryConfirmation: SellerApplicationAddressEntryConfirmationStepInput; buyingInterest?: never; condition?: never; confirm?: never; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never;   /** Interest in buying another home. */
  buyingInterest: SellerApplicationBuyingInterestStepInput; condition?: never; confirm?: never; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never;   /** Room and exterior condition step. */
  condition: SellerApplicationConditionStepInput; confirm?: never; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never;   /** Home facts confirmation step. */
  confirm: SellerApplicationConfirmStepInput; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never;   /** Seller, HOA, utility, and eligibility details step. */
  details: SellerApplicationDetailsStepInput; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never; details?: never;   /** Property eligibility conditions. */
  eligibility: SellerApplicationEligibilityStepInput; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never; details?: never; eligibility?: never;   /** Home features step. */
  features: SellerApplicationFeaturesStepInput; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never; details?: never; eligibility?: never; features?: never;   /** Homebuilder sales associate and community details. */
  homebuilderDetails: SellerApplicationHomebuilderDetailsStepInput; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never;   /** Relationship with a homebuilder. */
  homebuilderRelationship: SellerApplicationHomebuilderRelationshipStepInput; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never;   /** Selected homebuilder. */
  homebuilderSelection: SellerApplicationHomebuilderSelectionStepInput; mortgageQuoteInterest?: never; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never;   /** Interest in an Opendoor Home Loans mortgage quote. */
  mortgageQuoteInterest: SellerApplicationMortgageQuoteInterestStepInput; repairs?: never; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never;   /** Home repair issues step. */
  repairs: SellerApplicationRepairsStepInput; upgrades?: never; }
  |  { addressEntryConfirmation?: never; buyingInterest?: never; condition?: never; confirm?: never; details?: never; eligibility?: never; features?: never; homebuilderDetails?: never; homebuilderRelationship?: never; homebuilderSelection?: never; mortgageQuoteInterest?: never; repairs?: never;   /** Major home renovations. */
  upgrades: SellerApplicationUpgradesStepInput; };

/** Seller application identifier and exactly one onboarding step to update without advancing completion progress. */
export type SellerApplicationStepUpdateInput = {
  /** Seller application receiving the updated step. */
  applicationId: Types.Scalars['UUID']['input'];
  /** Exactly one seller application step to update. */
  step: SellerApplicationStepInput;
};

/** A marketing or acquisition event associated with application entry. */
export type SellerApplicationTrackingEventInput = {
  /** Keyword associated with the tracked event. */
  keyword: Types.Scalars['NonEmptyString']['input'];
  /** UTC timestamp when the event was tracked. */
  trackedAt: Types.Scalars['DateTime']['input'];
};

/** Whether the seller has made major renovations to the property. */
export type SellerApplicationUpgradesStepInput = {
  /** Whether the seller has made major renovations. */
  hasUpgrades: Types.Scalars['Boolean']['input'];
};

/** The eligibility or workflow reason associated with a seller product outcome. */
export type SellerDenialReason =
  /** An active offer already exists for the property. */
  | 'ACTIVE_OFFER'
  /** The property address is outside supported intake criteria. */
  | 'ADDRESS'
  /** A manually overridden address failed product eligibility. */
  | 'ADDRESS_OVERRIDE'
  /** The seller relationship with an agent does not meet product requirements. */
  | 'AGENT_RELATION'
  /** The home is in an unsupported age-restricted community. */
  | 'AGE_RESTRICTED_COMMUNITY'
  /** The home is in an age-restricted seasonal community. */
  | 'AGE_RESTRICTED_SEASONAL'
  /** The home has asbestos siding that is not supported. */
  | 'ASBESTOS_SIDING'
  /** The home was built before the supported construction year. */
  | 'BEFORE_1960'
  /** The property has below-market-rate ownership restrictions. */
  | 'BELOW_MARKET_RATE_OWNERSHIP'
  /** A buyer is not available for the property. */
  | 'BUYER_UNAVAILABLE'
  /** The property uses a cesspool that is not supported. */
  | 'CESSPOOL'
  /** A COVID-19 eligibility rule prevents the offering. */
  | 'COVID_19'
  /** The lead was denied for an Opendoor Direct offer. */
  | 'DENIED_FOR_OPENDOOR_DIRECT'
  /** The lead was denied for Sell with Upside. */
  | 'DENIED_FOR_SELL_WITH_UPSIDE'
  /** A related eligibility rule caused denial. */
  | 'DEPENDENT_RULESET'
  /** A duplicate seller lead already exists for the property. */
  | 'DUPLICATE_LEAD'
  /** The home dwelling type is not supported. */
  | 'DWELLING_TYPE'
  /** Projected home economics do not meet the expected margin threshold. */
  | 'EXPECTED_LGM'
  /** Property fees exceed the supported amount. */
  | 'FEES_TOO_HIGH'
  /** The home has fire damage that is not supported. */
  | 'FIRE_DAMAGE'
  /** The property has a flood history that is not supported. */
  | 'FLOOD_HISTORY'
  /** The property is in an unsupported flood zone. */
  | 'FLOOD_ZONE'
  /** The home has foundation concerns that are not supported. */
  | 'FOUNDATION'
  /** The home is in a gated community that this product does not support. */
  | 'GATED_COMMUNITY'
  /** The gated community requires a guard arrangement that is not supported. */
  | 'GUARD_GATED_COMMUNITY'
  /** The homeowners association does not meet product requirements. */
  | 'INELIGIBLE_HOA'
  /** The seller is already under contract with Opendoor. */
  | 'IN_CONTRACT_WITH_OPENDOOR'
  /** There are not enough comparable homes to support an offer. */
  | 'LACK_OF_ADEQUATE_COMPS'
  /** The home has leased solar panels that are not supported. */
  | 'LEASED_SOLAR_PANELS'
  /** The property is already listed by Opendoor. */
  | 'LISTED_BY_OPENDOOR'
  /** The property has livestock that is not supported. */
  | 'LIVESTOCK'
  /** The lot size is outside the supported range. */
  | 'LOT_SQ_FT'
  /** The property listing status is not supported. */
  | 'MLS_LISTING_STATUS'
  /** The property is a mobile or manufactured home that is not supported. */
  | 'MOBILE_MANUFACTURED_HOME'
  /** The dwelling type is outside the acquisition criteria. */
  | 'NON_BUYBOX_DWELLING_TYPE'
  /** The seller does not meet the product engagement requirements. */
  | 'NON_SERIOUS_SELLER'
  /** The property is outside the auto-denial listing experience. */
  | 'NOT_IN_AUTODENIAL_LISTING_EXP'
  /** The property is outside the List With Opendoor pilot. */
  | 'NOT_IN_LWOD_PILOT'
  /** The property is outside the Sell with Upside pilot. */
  | 'NOT_IN_SELL_WITH_UPSIDE_PILOT'
  /** The property is outside the side-by-side product experience. */
  | 'NOT_IN_SIDE_BY_SIDE_EXP'
  /** No denial reason applies. */
  | 'NO_DENIAL'
  /** Another offer is already being prepared for the property. */
  | 'OFFER_IN_PROGRESS'
  /** The offer falls outside expected pricing bounds. */
  | 'OFFER_OUTLIER'
  /** The offering was denied for another recorded reason. */
  | 'OTHER_DENIAL'
  /** A detailed property or seller criterion caused denial. */
  | 'OTHER_DENIAL_DETAIL'
  /** A general property or seller criterion caused denial. */
  | 'OTHER_DENIAL_GENERAL'
  /** Another high-risk property condition prevents the offering. */
  | 'OTHER_HIGH_RISK'
  /** The property is already owned by Opendoor. */
  | 'OWNED_BY_OPENDOOR'
  /** A partnership rule prevents this product from being offered. */
  | 'PARTNERSHIP'
  /** The home has polybutylene plumbing that is not supported. */
  | 'POLYBUTYLENE_PIPING'
  /** An offer is still being prepared for the seller. */
  | 'PREPARING_OFFER'
  /** The home has a private pool that is not supported. */
  | 'PRIVATE_POOL'
  /** The product is not active for this property. */
  | 'PRODUCT_INACTIVE'
  /** The property is outside the product experiment. */
  | 'PRODUCT_NOT_IN_EXPERIMENT'
  /** The lead qualified for List With Opendoor instead. */
  | 'QUALIFIED_FOR_LWOD'
  /** The lead qualified for an Opendoor Direct offer instead. */
  | 'QUALIFIED_FOR_OPENDOOR_DIRECT'
  /** The property had a recent Opendoor home visit. */
  | 'RECENT_OPENDOOR_WALK'
  /** The property is reserved for an external broker. */
  | 'RESERVED_FOR_EXTERNAL_BROKER'
  /** The property is in a region excluded by risk policy. */
  | 'RISK_PROHIBITED_REGION'
  /** The seller timeline is outside the product requirements. */
  | 'SALE_TIMELINE'
  /** The seller lead requires manual review before qualification. */
  | 'SELLER_TRIAGE'
  /** The home uses a septic system that is not supported. */
  | 'SEPTIC'
  /** The home has a shared entrance that is not supported. */
  | 'SHARED_ENTRANCE'
  /** The home shares a lot in a way that is not supported. */
  | 'SHARED_LOT'
  /** The property is occupied by a tenant in a way this product does not support. */
  | 'TENANT_OCCUPIED'
  /** The property is part of a trade-in flow that is not supported here. */
  | 'TRADE_IN'
  /** The property cannot be valued reliably enough for an offer. */
  | 'UNABLE_TO_VALUE'
  /** The property has an underground oil tank that is not supported. */
  | 'UNDERGROUND_OIL_TANK'
  /** The property has an ownership structure that is not supported. */
  | 'UNIQUE_OWNERSHIP_STRUCTURE'
  /** The home has an unpermitted addition. */
  | 'UNPERMITTED_ADDITION'
  /** The estimated home value is above the product range. */
  | 'VALUATION_TOO_HIGH'
  /** The estimated home value is below the product range. */
  | 'VALUATION_TOO_LOW'
  /** The estimated value is too low for a currently listed property. */
  | 'VALUATION_TOO_LOW_LISTED'
  /** The home uses well water that is not supported. */
  | 'WELL_WATER'
  /** The seller is working with a homebuilder in a way this product does not support. */
  | 'WORKING_WITH_HOMEBUILDER'
  /** The home year is outside the supported range. */
  | 'YEAR_BUILT';

/** The acquisition or product journey that created a seller lead. */
export type SellerLeadChannel =
  /** A real estate agent requested a seller offer. */
  | 'AGENT_REQUEST'
  /** A real estate agent requested an offer for a listed home. */
  | 'AGENT_REQUEST_LISTED'
  /** The seller entered through an Opendoor mobile app. */
  | 'APP'
  /** The seller entered through a combined buy-and-sell flow. */
  | 'BUY_SELL_FLOW'
  /** The seller entered through an Opendoor home-selling landing page. */
  | 'CAKE_LANDING_PAGE'
  /** The lead records a privacy opt-out request. */
  | 'CCPA_OPT_OUT'
  /** Lead source is not more specifically classified. */
  | 'DEFAULT'
  /** The lead came from seller email outreach. */
  | 'EMAIL_PROSPECTING'
  /** The seller entered through the exclusive-offers experience. */
  | 'EXCLUSIVE_OFFERS'
  /** The lead was created by the Flywheel acquisition experience. */
  | 'FLYWHEEL'
  /** The seller entered through the Home Reserve experience. */
  | 'HOME_RESERVE'
  /** The seller requested a home-value estimate. */
  | 'HOME_VALUE'
  /** The lead came from an Opendoor-backed offer flow. */
  | 'OPENDOOR_BACKED_OFFER'
  /** The seller received an outbound offer while listed. */
  | 'OUTBOUND_OFFERS_LISTED'
  /** The seller received an outbound underwritten offer. */
  | 'OUTBOUND_OFFERS_UNDERWRITTEN'
  /** The lead came through a partner API. */
  | 'PARTNER_API'
  /** The lead came from a REIT bulk request. */
  | 'REIT_BULK_REQUEST'
  /** The listed lead came from a REIT bulk request. */
  | 'REIT_BULK_REQUEST_LISTED'
  /** The seller requested an offer while the home was listed. */
  | 'SELLER_REQUEST_LISTED'
  /** The seller entered through a trade-in journey. */
  | 'TRADE_IN';

/** The seller’s expected timing for selling the home. */
export type SellerTimeline =
  /** The seller wants to sell as soon as possible. */
  | 'ASAP'
  /** The seller is exploring options without a specific timeline. */
  | 'JUST_BROWSING'
  /** The seller expects to sell in two to four weeks. */
  | 'WEEKS_2_4'
  /** The seller expects to sell in four to six weeks. */
  | 'WEEKS_4_6'
  /** The seller expects to sell in more than six weeks. */
  | 'WEEKS_6_PLUS';

/** Address parts used to find a normalized address. */
export type StandardizeAddressInput = {
  /** City portion of the address. */
  city: Types.Scalars['NonEmptyString']['input'];
  /** Postal code for the address. */
  postalCode: Types.Scalars['PostalCode']['input'];
  /** State or region portion of the address. */
  state: Types.Scalars['NonEmptyString']['input'];
  /** Street portion of the address. */
  street: Types.Scalars['NonEmptyString']['input'];
  /** Unit or apartment number, when applicable. */
  unitNumber?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Type of unit or sub-premise, when applicable. */
  unitType?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
};

export type StandardizeSellerAddressQueryVariables = Exact<{
  input: Types.StandardizeAddressInput;
}>;


export type StandardizeSellerAddressQuery = { address: { standardize: { city: Types.Scalars['String']['output'], id: Types.Scalars['ID']['output'], latitude: Types.Scalars['Latitude']['output'] | null, longitude: Types.Scalars['Longitude']['output'] | null, postalCode: Types.Scalars['PostalCode']['output'], state: Types.Scalars['String']['output'], street: Types.Scalars['String']['output'], unitNumber: Types.Scalars['String']['output'] | null, validationResults: Array<Types.AddressValidationResult> } | null } };

export type SellerAddressRequiresUnitSelectionQueryVariables = Exact<{
  addressId: Types.Scalars['UUID']['input'];
}>;


export type SellerAddressRequiresUnitSelectionQuery = { address: { requiresUnitSelection: Types.Scalars['Boolean']['output'] } };

export type StartSellerApplicationMutationVariables = Exact<{
  input: Types.SellerApplicationStartInput;
}>;


export type StartSellerApplicationMutation = { sellerApplicationStart:
    | { __typename: 'InvalidInputError' }
    | { __typename: 'NotFoundError' }
    | { __typename: 'SellerApplication', id: Types.Scalars['ID']['output'], address: { id: Types.Scalars['ID']['output'], postalCode: Types.Scalars['PostalCode']['output'] } | null, productOfferings: Array<{ denialReason: Types.SellerDenialReason }> }
    | { __typename: 'UnauthorizedError' }
   };

export type ConfirmSellerAddressEntryMutationVariables = Exact<{
  input: Types.SellerApplicationStepUpdateInput;
}>;


export type ConfirmSellerAddressEntryMutation = { sellerApplicationStepUpdate:
    | { __typename: 'InvalidInputError' }
    | { __typename: 'NotFoundError' }
    | { __typename: 'SellerApplication', id: Types.Scalars['ID']['output'] }
    | { __typename: 'UnauthorizedError' }
   };

export type SellerAddressListingRouteQueryVariables = Exact<{
  addressId: Types.Scalars['UUID']['input'];
}>;


export type SellerAddressListingRouteQuery = { marketplace: { listingDetails: { summary: { addressId: Types.Scalars['UUID']['output'], slug: Types.Scalars['String']['output'] | null } } | null } };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const StandardizeSellerAddressDocument = /*#__PURE__*/ new TypedDocumentString(`
    query StandardizeSellerAddress($input: StandardizeAddressInput!) {
  address {
    standardize(input: $input) {
      city
      id
      latitude
      longitude
      postalCode
      state
      street
      unitNumber
      validationResults
    }
  }
}
    `) as unknown as TypedDocumentString<StandardizeSellerAddressQuery, StandardizeSellerAddressQueryVariables>;
export const SellerAddressRequiresUnitSelectionDocument = /*#__PURE__*/ new TypedDocumentString(`
    query SellerAddressRequiresUnitSelection($addressId: UUID!) {
  address {
    requiresUnitSelection(addressId: $addressId)
  }
}
    `) as unknown as TypedDocumentString<SellerAddressRequiresUnitSelectionQuery, SellerAddressRequiresUnitSelectionQueryVariables>;
export const StartSellerApplicationDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation StartSellerApplication($input: SellerApplicationStartInput!) {
  sellerApplicationStart(input: $input) {
    __typename
    ... on SellerApplication {
      address {
        id
        postalCode
      }
      id
      productOfferings {
        denialReason
      }
    }
  }
}
    `) as unknown as TypedDocumentString<StartSellerApplicationMutation, StartSellerApplicationMutationVariables>;
export const ConfirmSellerAddressEntryDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation ConfirmSellerAddressEntry($input: SellerApplicationStepUpdateInput!) {
  sellerApplicationStepUpdate(input: $input) {
    __typename
    ... on SellerApplication {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<ConfirmSellerAddressEntryMutation, ConfirmSellerAddressEntryMutationVariables>;
export const SellerAddressListingRouteDocument = /*#__PURE__*/ new TypedDocumentString(`
    query SellerAddressListingRoute($addressId: UUID!) {
  marketplace {
    listingDetails(addressId: $addressId) {
      summary {
        addressId
        slug
      }
    }
  }
}
    `) as unknown as TypedDocumentString<SellerAddressListingRouteQuery, SellerAddressListingRouteQueryVariables>;